import { Component, Vue } from 'vue-property-decorator';
import { WhatsAppPlatform } from 'ignite360-core';

import {
  Session,
  Interaction,
  InboxLog,
  Project,
  getResponsePlatform,
  getRequestPlatform,
  getPlatformResponseOutputTemplate,
  getOutputText,
  getPlatformRequest,
} from '@jovotech/inbox-core';
import type { OutputTemplate } from '@jovotech/output';
import { FormatUtil } from '@/inbox/FormatUtil';
import { Bot } from 'ignite360-core';
import { InboxApi } from '@/api/InboxApi';

@Component
export class InboxMixin extends Vue {
  getImage(conversation: InboxLog) {
    if (this.nameMap[conversation.userId] && this.nameMap[conversation.userId].image) {
      // TODO base_url
      return `/avatars/${this.nameMap[conversation.userId].image}?random=${Math.random()}`;
    }
  }
  shortenUserId(inboxLog: InboxLog) {
    return FormatUtil.shortenUserId(inboxLog.userId);
  }
  get selectedConversations() {
    return this.$store.state.inbox.selectedUserConversations;
  }

  get bot(): Bot | undefined {
    return this.$store.state.bots.bots.find((bot: Bot) => bot.id === this.$route.params.id);
  }

  get conversation(): InboxLog | undefined {
    if (this.selectedConversations.length > 0) {
      return this.selectedConversations[0];
    }
    return undefined;
  }

  get selectedInteraction(): Interaction | null {
    return this.$store.state.inbox.selectedInteraction;
  }

  getConversations(): Interaction[] {
    return this.$store.state.inbox.conversations;
  }

  get interactions() {
    const interactions: Record<string, Interaction> = {};

    const isSessionStart = (index: number) => {
      return (
        index === 0 ||
        this.selectedConversation[index - 1].sessionId !==
          this.selectedConversation[index].sessionId
      );
    };

    for (let i = 0; i < this.selectedConversation.length; i++) {
      const conversation = this.selectedConversation[i];

      if (!interactions[conversation.requestId]) {
        interactions[conversation.requestId] = {
          requestId: conversation.requestId,
          logs: [],
          start: new Date(conversation.createdAt),
          hasSessionStarted: isSessionStart(i),
        };
      }
      interactions[conversation.requestId].logs.push(conversation);
    }

    // sort turns by turnStart
    return Object.values(interactions).sort((a, b) => {
      return a.start.getTime() - b.start.getTime();
    });
  }

  get sessions() {
    const sessions: Record<string, Session> = {};

    for (let i = 0; i < this.interactions.length; i++) {
      const turn = this.interactions[i];
      const firstSessionLog = turn.logs[0];

      if (!sessions[firstSessionLog.sessionId]) {
        sessions[firstSessionLog.sessionId] = {
          sessionId: firstSessionLog.sessionId,
          sessionStart: new Date(turn.logs[0].createdAt),
          interactions: [],
        };
      }
      sessions[firstSessionLog.sessionId].interactions.push(turn);
    }

    // sort turns by turnStart
    return Object.values(sessions).sort((a, b) => {
      return a.sessionStart.getTime() - b.sessionStart.getTime();
    });
  }

  get project(): Project {
    return this.$store.state.inbox.selectedProject;
  }

  get projects(): Project[] {
    return this.$store.state.inbox.projects;
  }

  getResponsePlatform(inboxLog: InboxLog) {
    return getResponsePlatform(inboxLog);
  }

  getRequestPlatform(inboxLog: InboxLog) {
    if (inboxLog.platform === 'WhatsAppPlatform') {
      return new WhatsAppPlatform({
        pageAccessToken: '',
      });
    }
    return getRequestPlatform(inboxLog);
  }

  async getPlatformResponseOutputTemplate(inboxLog: InboxLog): Promise<OutputTemplate[]> {
    return getPlatformResponseOutputTemplate(inboxLog);
  }

  getOutputText(output: OutputTemplate): string {
    return getOutputText(output);
  }

  getMessageChunks(str: string) {
    return FormatUtil.getMessageChunks(str);
  }

  getPlatformRequest(inboxLog: InboxLog) {
    // For WhatsApp, we need to extract the message from the changes array
    if (
      inboxLog.platform === 'WhatsAppPlatform' &&
      inboxLog.payload?.changes?.[0]?.value?.messages?.[0]
    ) {
      const message = inboxLog.payload.changes[0].value.messages[0];
      return {
        type: 'user',
        text: message.type === 'text' ? message.text?.body : message.type,
      };
    }
    return getPlatformRequest(inboxLog);
  }

  async selectConversation() {
    if (this.$route.params.userId) {
      await this.$store.dispatch('inbox/fetchUserConversations', {
        userId: this.$route.params.userId,
        projectId: this.bot?.id,
      });

      await InboxApi.markConversationAsChecked(this.bot?.id!, this.$route.params.userId);

      await this.$store.dispatch('inbox/fetchCheckedConversations', {
        projectId: this.bot?.id,
      });
    }
  }

  get selectedConversation(): InboxLog[] {
    return this.$store.state.inbox.selectedUserConversations;
  }

  getLogByType(interaction: Interaction, type: string) {
    return interaction.logs.find((item: InboxLog) => item.type === type);
  }

  get isLiveMode(): boolean {
    return false;
  }

  get nameMap(): Record<
    string,
    {
      name: string;
      image?: string;
    }
  > {
    return this.$store.state.inbox.nameMap;
  }

  get isMobile() {
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      navigator.userAgent.toLowerCase(),
    );
  }
}
